<template>
  <div
    class="absolute pointer-events-auto cursor-pointer transition-all"
    :style="pipStyle"
    @mousedown.stop="handleMouseDown"
    @click.stop="handleClick"
  >
    <!-- Main stacked pip indicator -->
    <div
      class="w-7 h-7 rounded-full border-2 shadow-lg transition-all flex items-center justify-center relative"
      :class="{
        'border-primary ring-2 ring-primary/50': isSelected,
        'border-white': !isSelected
      }"
      :style="{ backgroundColor: averageColor }"
    >
      <!-- Show X when menu is open, otherwise show count -->
      <span v-if="showRadialMenu" class="text-sm font-bold text-white drop-shadow-md">x</span>
      <span v-else class="text-xs font-bold text-white drop-shadow-md">
        {{ stack.lights.length }}
      </span>
      
      <!-- Stacked indicator rings (shows that multiple lights are here) - hide when menu open -->
      <template v-if="!showRadialMenu">
        <div
          v-for="(_, i) in Math.min(stack.lights.length - 1, 2)"
          :key="i"
          class="absolute rounded-full border-2 border-white/50"
          :style="{
            width: `${28 + (i + 1) * 4}px`,
            height: `${28 + (i + 1) * 4}px`,
            top: `${-2 - (i + 1) * 2}px`,
            left: `${-2 - (i + 1) * 2}px`
          }"
        />
      </template>
    </div>
    
    <!-- Radial menu (shown on click) -->
    <div
      v-if="showRadialMenu"
      class="absolute z-50"
      :style="radialMenuStyle"
      data-radial-menu
    >
      <div class="relative w-48 h-48">
        <!-- Radial items - using Badge component -->
        <Badge
          v-for="(item, index) in stack.lights"
          :key="item.light.id"
          variant="secondary"
          class="absolute cursor-pointer transition-all hover:scale-105 text-[10px] py-0"
          :class="{
            'ring-1 ring-primary': selectedLightIds.has(item.light.id)
          }"
          :style="getRadialItemStyle(index, stack.lights.length, item)"
          @mousedown.stop="handleRadialItemMouseDown($event, item.light)"
          @click.stop="handleRadialItemClick(item.light)"
        >
          {{ item.light.name }}
        </Badge>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import type { Light } from '@/stores/lights'
import type { StackedGroup, LightWithPosition } from '@/composables/useLightPositioning'
import { hsvToRGB } from '@/lib/colorConversion'
import { getPipStyle } from '@/lib/coordinateUtils'
import { Badge } from '@/components/ui/badge'

interface Props {
  stack: StackedGroup
  circleRadius: number
  circleCenterX: number
  circleCenterY: number
  isSelected: boolean
  selectedLightIds: Set<string>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  dragStart: [event: MouseEvent, lights: Light[]]
  click: [lights: Light[]]
  lightDragStart: [event: MouseEvent, light: Light]
  lightClick: [light: Light]
  disband: [lights: Light[]]
}>()

const showRadialMenu = ref(false)
const isDragging = ref(false)
const startMousePos = ref<{ x: number; y: number } | null>(null)

// Calculate pixel position using the same utility as LightPipSingle
const pipStyle = computed(() => {
  const style = getPipStyle(
    props.stack.position,
    {
      centerX: props.circleCenterX,
      centerY: props.circleCenterY,
      radius: props.circleRadius
    },
    28 // Stack pip is slightly larger
  )
  return style
})

// Calculate average color from all lights in stack
const averageColor = computed(() => {
  let totalHue = 0
  let totalSat = 0
  let count = 0

  for (const item of props.stack.lights) {
    const state = item.light.current_state
    if (state?.hue != null && state?.sat != null) {
      totalHue += state.hue
      totalSat += state.sat
      count++
    }
  }

  // If no lights have hue/sat, use a nice default color (warm orange)
  if (count === 0) {
    // Try to get position-based color from the stack position
    const pos = props.stack.position
    // Position is in -1 to 1 range for color wheel
    const angle = Math.atan2(pos.y, pos.x)
    const radius = Math.sqrt(pos.x * pos.x + pos.y * pos.y)
    const h = ((angle * 180 / Math.PI) + 360) % 360
    const s = Math.min(1, radius)
    const rgb = hsvToRGB({ h, s, v: 1 })
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }

  const avgHue = totalHue / count / 65535 * 360
  const avgSat = totalSat / count / 254
  const rgb = hsvToRGB({ h: avgHue, s: avgSat, v: 1 })
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
})

const radialMenuStyle = computed(() => ({
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)'
}))

function getRadialItemStyle(index: number, total: number, _item: LightWithPosition) {
  const angleStep = (2 * Math.PI) / total
  const angle = -Math.PI / 2 + index * angleStep // Start from top
  const radius = 75 // Distance from center

  const x = Math.cos(angle) * radius
  const y = Math.sin(angle) * radius

  return {
    left: `calc(50% + ${x}px)`,
    top: `calc(50% + ${y}px)`,
    transform: 'translate(-50%, -50%)'
  }
}

function getLightColor(item: LightWithPosition): string {
  const state = item.light.current_state
  if (state?.hue != null && state?.sat != null) {
    const h = state.hue / 65535 * 360
    const s = state.sat / 254
    const rgb = hsvToRGB({ h, s, v: 1 })
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }
  return '#888888'
}

function handleMouseDown(event: MouseEvent) {
  if (showRadialMenu.value) {
    // Don't start a new drag when radial menu is open
    return
  }
  // Store the start position, we'll emit drag only if user moves significantly
  startMousePos.value = { x: event.clientX, y: event.clientY }
  isDragging.value = false
  
  // Attach global listeners for drag detection
  document.addEventListener('mousemove', handleDocumentMouseMove)
  document.addEventListener('mouseup', handleDocumentMouseUp)
}

function handleDocumentMouseMove(event: MouseEvent) {
  if (!startMousePos.value || isDragging.value) return
  
  const dx = event.clientX - startMousePos.value.x
  const dy = event.clientY - startMousePos.value.y
  const distance = Math.sqrt(dx * dx + dy * dy)
  
  // Only start drag if moved more than 5 pixels
  if (distance > 5) {
    isDragging.value = true
    // Remove listeners since drag is now handled by parent
    document.removeEventListener('mousemove', handleDocumentMouseMove)
    document.removeEventListener('mouseup', handleDocumentMouseUp)
    emit('dragStart', event, props.stack.lights.map(l => l.light))
  }
}

function handleDocumentMouseUp() {
  // Clean up listeners
  document.removeEventListener('mousemove', handleDocumentMouseMove)
  document.removeEventListener('mouseup', handleDocumentMouseUp)
  startMousePos.value = null
  isDragging.value = false
}

function handleClick(event: MouseEvent) {
  // Prevent click from bubbling when toggling menu
  event.stopPropagation()
  // Only toggle menu if we weren't dragging
  if (!isDragging.value) {
    if (showRadialMenu.value) {
      // If menu is open and clicking the X, trigger disband
      handleDisband()
    } else {
      showRadialMenu.value = true
    }
  }
  isDragging.value = false
}

function handleRadialItemMouseDown(event: MouseEvent, light: Light) {
  event.preventDefault()
  event.stopPropagation()
  showRadialMenu.value = false
  // Use nextTick to ensure menu closes before drag starts
  emit('lightDragStart', event, light)
}

function handleRadialItemClick(light: Light) {
  emit('lightClick', light)
}

function handleDisband() {
  showRadialMenu.value = false
  emit('disband', props.stack.lights.map(l => l.light))
}

// Close menu when clicking outside - but not on radial items
function handleOutsideClick(event: MouseEvent) {
  if (!showRadialMenu.value) return
  
  // Check if click is inside the radial menu container
  const target = event.target as HTMLElement
  const isInsideRadial = target.closest('[data-radial-menu]')
  
  if (!isInsideRadial) {
    showRadialMenu.value = false
  }
}

onMounted(() => {
  // Use bubbling phase (false) so radial item handlers run first
  document.addEventListener('mousedown', handleOutsideClick, false)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideClick, false)
})
</script>
